import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';
import { logError } from '../../../utils/logger';
import { MasterConfig, MasterFieldDef } from './masters.config';

export interface MasterRow {
  id: number;
  label: string;
}

interface MasterFormDialogProps {
  open: boolean;
  master: MasterConfig;
  /** The row being edited; null opens the dialog in create mode. */
  editingId: number | null;
  /** Prefills the name when the user typed a value that matched nothing. */
  initialLabel?: string;
  onClose: () => void;
  /** Handed the saved row so the caller can select it without waiting for a refetch. */
  onSaved: (row: MasterRow) => void;
}

type Values = Record<string, unknown>;

const blankValues = (fields: readonly MasterFieldDef[]): Values =>
  fields.reduce<Values>((acc, field) => {
    acc[field.name] = field.defaultValue ?? (field.type === 'boolean' ? true : '');
    return acc;
  }, {});

/** API rows come back camelCase; the form is keyed by the snake_case request key. */
const toFormValues = (row: any, fields: readonly MasterFieldDef[]): Values =>
  fields.reduce<Values>((acc, field) => {
    const camel = field.name.replace(/_([a-z])/g, (_match, c: string) => c.toUpperCase());
    const raw = row?.[camel] ?? row?.[field.name];
    acc[field.name] = field.type === 'boolean' ? raw !== false : (raw ?? '');
    return acc;
  }, {});

/**
 * Add or edit one master row without leaving the job edit screen.
 *
 * Carries that master's full field set — the same one its own admin screen shows — so a
 * row created here needs no follow-up visit to Contact Masters or Reference Data.
 *
 * On edit it fetches the full row first rather than seeding from the option list, which
 * only carries `{ id, label }`: a form built from that would blank every field the list
 * does not know about.
 */
export default function MasterFormDialog({
  open,
  master,
  editingId,
  initialLabel = '',
  onClose,
  onSaved,
}: MasterFormDialogProps) {
  const { fields, labelKey, entityName, client } = master;
  const labelField = fields[0].name;

  const [values, setValues] = useState<Values>(() => blankValues(fields));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setError(null);

    if (editingId == null) {
      setValues({ ...blankValues(fields), [labelField]: initialLabel });
      return;
    }

    let active = true;
    setLoading(true);
    client
      .get(editingId)
      .then((row) => {
        if (active) setValues(toFormValues(row, fields));
      })
      .catch((err) => {
        if (!active) return;
        setError(
          logError({
            message: `Failed to load ${entityName.toLowerCase()}`,
            error: err,
            code: 'MASTER_FETCH_FAILED',
          })
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // `initialLabel` is only a seed for a fresh create; re-running on it would undo typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId, master]);

  const setField = (name: string, value: unknown) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const handleSave = async () => {
    const name = String(values[labelField] ?? '').trim();
    if (!name) {
      setError(`${fields[0].label} is required.`);
      return;
    }

    const payload = fields.reduce<Record<string, unknown>>((acc, field) => {
      const value = values[field.name];

      // Booleans always travel — `false` is a real choice, not an empty field.
      if (field.type === 'boolean') {
        acc[field.name] = Boolean(value);
        return acc;
      }

      const text = String(value ?? '').trim();
      // Everything else only travels when filled: on edit each controller patches just
      // what it is given, so an untouched blank must not clear what is already stored.
      if (!text) return acc;

      acc[field.name] = field.type === 'number' ? Number(text) : text;
      return acc;
    }, {});

    setSaving(true);
    setError(null);
    try {
      const saved =
        editingId == null ? await client.create(payload) : await client.update(editingId, payload);

      onSaved({ id: (saved as any).id, label: (saved as any)[labelKey] ?? name });
      onClose();
    } catch (err) {
      setError(
        logError({
          message: `Failed to save ${entityName.toLowerCase()}`,
          error: err,
          code: 'MASTER_SAVE_FAILED',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field: MasterFieldDef) => {
    if (field.type === 'boolean') {
      return (
        <FormControlLabel
          key={field.name}
          control={
            <Switch
              checked={Boolean(values[field.name])}
              disabled={loading || saving}
              onChange={(e) => setField(field.name, e.target.checked)}
            />
          }
          label={field.label}
        />
      );
    }

    if (field.type === 'autocomplete') {
      const options = field.options ?? [];
      const current = values[field.name] == null ? '' : String(values[field.name]);
      return (
        <Autocomplete
          key={field.name}
          size='small'
          options={options}
          value={options.find((option) => option.value === current) ?? null}
          onChange={(_event, option) => setField(field.name, option ? option.value : '')}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, picked) => option.value === picked.value}
          disabled={loading || saving}
          openOnFocus
          autoHighlight
          renderInput={(params) => (
            <TextField {...params} label={field.label} required={field.required} helperText={field.helperText} placeholder='Search…' />
          )}
        />
      );
    }

    const isTextarea = field.type === 'textarea';

    return (
      <TextField
        key={field.name}
        size='small'
        label={field.label}
        required={field.required}
        helperText={field.helperText}
        disabled={loading || saving}
        type={field.type === 'number' ? 'number' : 'text'}
        multiline={isTextarea}
        minRows={isTextarea ? 2 : undefined}
        value={values[field.name] ?? ''}
        onChange={(e) => setField(field.name, e.target.value)}
        fullWidth
        // Addresses and notes get the full width; the short fields pair up.
        sx={isTextarea ? { gridColumn: '1 / -1' } : undefined}
      />
    );
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth='sm'>
      <DialogTitle>{editingId == null ? `Add ${entityName}` : `Edit ${entityName}`}</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity='error' sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            columnGap: 2,
            rowGap: 2.5,
            paddingTop: 1,
          }}
        >
          {fields.map(renderField)}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: '8px 24px 16px' }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSave} disabled={saving || loading}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
